import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { bolusnotesApi } from '../services/api';
import './Followers.css';

interface Follower {
  id: string;
  email: string;
  type: 'Viewer' | 'Contributor';
  status: 'Pending' | 'Active';
}

const Followers: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteType, setInviteType] = useState<'Viewer' | 'Contributor'>('Viewer');
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Mock data for UI demonstration purposes until fully wired up
  const [followers, setFollowers] = useState<Follower[]>([
    { id: '1', email: 'doctor@example.com', type: 'Viewer', status: 'Active' },
    { id: '2', email: 'family@example.com', type: 'Contributor', status: 'Pending' }
  ]);
  const [following] = useState([
    { id: '3', ownerName: 'Alice', type: 'Viewer', status: 'Active' }
  ]);

  const loadFollowers = async () => {
    try {
      // In reality we'd load this from bolusnotesApi.followers.list()
      // const data = await bolusnotesApi.followers.list();
      // setFollowers(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadFollowers();
  }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setLoading(true);
    setMessage(null);
    try {
      // await bolusnotesApi.followers.invite(inviteEmail, inviteType);
      
      // MOCK
      setFollowers([...followers, {
        id: Math.random().toString(),
        email: inviteEmail,
        type: inviteType,
        status: 'Pending'
      }]);
      setMessage({ text: `Invitation sent to ${inviteEmail}`, type: 'success' });
      setShowInviteModal(false);
      setInviteEmail('');
    } catch (err: any) {
      setMessage({ text: 'Failed to send invite', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      // await bolusnotesApi.followers.revoke(id);
      setFollowers(followers.filter(f => f.id !== id));
    } catch (err) {
      console.error('Revoke failed', err);
    }
  };

  return (
    <div className="ios-dashboard-container followers-container">
      <header className="ios-header">
        <div className="header-left">
          <button className="icon-btn" onClick={() => navigate('/dashboard')}>←</button>
          <h1>Followers</h1>
        </div>
        <div className="header-actions">
          <ThemeToggle placement="header" />
          <button className="btn-setup highlight" onClick={() => setShowInviteModal(true)}>+ Invite</button>
        </div>
      </header>

      <main className="ios-content">
        {message && (
          <div className={`status-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <section className="follower-section">
          <h2>People following me</h2>
          {followers.length === 0 ? (
            <div className="empty-state">No followers yet.</div>
          ) : (
            <div className="follower-list">
              {followers.map(f => (
                <div key={f.id} className="follower-card">
                  <div className="follower-info">
                    <strong>{f.email}</strong>
                    <div className="follower-meta">
                      <span className={`status-badge ${f.status === 'Active' ? 'success' : 'pending'}`}>
                        {f.status}
                      </span>
                      <span className="type-badge">{f.type}</span>
                    </div>
                  </div>
                  <button className="btn-revoke" onClick={() => handleRevoke(f.id)}>Revoke</button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="follower-section">
          <h2>People I am following</h2>
          {following.length === 0 ? (
            <div className="empty-state">You are not following anyone.</div>
          ) : (
            <div className="follower-list">
              {following.map(f => (
                <div key={f.id} className="follower-card">
                  <div className="follower-info">
                    <strong>{f.ownerName}</strong>
                    <div className="follower-meta">
                      <span className="type-badge">{f.type}</span>
                    </div>
                  </div>
                  <button className="btn-setup" disabled>View Dashboard</button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {showInviteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Invite Follower</h2>
            <form onSubmit={handleInvite}>
              <div className="form-group">
                <label>Email Address</label>
                <input 
                  type="email" 
                  value={inviteEmail} 
                  onChange={e => setInviteEmail(e.target.value)} 
                  placeholder="Enter email..."
                  required 
                />
              </div>
              <div className="form-group">
                <label>Permission Level</label>
                <div className="radio-group" style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                  <label>
                    <input 
                      type="radio" 
                      checked={inviteType === 'Viewer'} 
                      onChange={() => setInviteType('Viewer')} 
                    /> Viewer
                  </label>
                  <label>
                    <input 
                      type="radio" 
                      checked={inviteType === 'Contributor'} 
                      onChange={() => setInviteType('Contributor')} 
                    /> Contributor
                  </label>
                </div>
                <p className="help-text">
                  {inviteType === 'Viewer' 
                    ? 'Can only view glucose data and entries.' 
                    : 'Can view data and also add/edit entries.'}
                </p>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowInviteModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Followers;
